import { useEffect, useState } from "react";
import { useApiContext } from 'context/ApiContext';

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import { MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DataTable from "examples/Tables/DataTableV2";

function Create({ parks, currentRole }) {
    const { setUserFormInfosAdm, postAPI, APIRequestType } = useApiContext();

    const [formValues, setFormValues] = useState({});
    const [initialValues, setInitialValues] = useState({});
    const [error, setError] = useState(false); // Etat pour gerer l'erreur visuelle

    const [userParks, setUserParks] = useState([]); // Liste des parcs de l'utilisateur
    const [availableParks, setAvailableParks] = useState([]); // Liste des parcs disponibles
    const [selectedPark, setSelectedPark] = useState(""); // Parc sélectionné

    const [phoneError, setPhoneError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [newPswdError, setNewPswdError] = useState(false);
    const [confirmPswdError, setConfirmPswdError] = useState(false);

    useEffect(() => {
        if (parks) {
            const formData = {
                role: currentRole === "AdminAT" ? "" : "AdminPark",
                lastName: "",
                name: "",
                phone: "",
                email: "",
                newPswd: "",
                confirmNewPswd: "",
            };

            setFormValues(formData);
            setInitialValues(formData);
            setAvailableParks(parks);
        }
    }, [parks]);

    const isValidEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const isStrongPassword = (password) => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        return regex.test(password);
    };

    const isFormValid = () => {
        const {
            role,
            lastName,
            name,
            phone,
            email,
            newPswd,
            confirmNewPswd
        } = formValues;

        const allFieldsFilled =
            role &&
            lastName &&
            name &&
            phone &&
            email &&
            newPswd &&
            confirmNewPswd;

        const noValidationErrors =
            !emailError &&
            !phoneError &&
            !newPswdError &&
            !confirmPswdError;

        const hasAtLeastOnePark = userParks.length > 0;

        if (currentRole === "AdminAT" && (formValues.role === "AdminAT" || formValues.role === "AdminEnterprise")) {
            return allFieldsFilled && noValidationErrors;
        } else if (formValues.role === "AdminPark") {
            return allFieldsFilled && noValidationErrors && hasAtLeastOnePark;
        }
    };

    const handleChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "lastName") {
            // Garder uniquement les lettres, lettres accentuées et le tiret
            value = value.replace(/[^a-zA-ZÀ-ÿ\s-]/g, "");
            // Limiter à 25 caractères
            value = value.slice(0, 25);
            // Capitaliser la première lettre de chaque mot
            value = value.toUpperCase();
        }

        if (field === "name") {
            value = value.replace(/[^a-zA-ZÀ-ÿ\s-]/g, "");
            value = value.slice(0, 25);
            value = value.replace(/(?:^|[\s-])\S/g, match => match.toUpperCase());
        }

        if (field === "phone") {
            // Supprimer tout sauf les chiffres
            value = value.replace(/\D/g, "");
            // Supprimer un éventuel 0 au début
            if (value.startsWith("0")) value = value.slice(1);
            // Limiter à 9 chiffres max
            value = value.slice(0, 9);
            // Mettre à jour l'erreur si ≠ 9 chiffres (uniquement s'il y a une saisie)
            setPhoneError(value.length > 0 && value.length !== 9);
        }

        if (field === "email") {
            value = value.trim(); // Supprime les espaces autour
            setEmailError(value.length > 0 && !isValidEmail(value));
        }

        if (field === "newPswd") {
            value = value;
            setNewPswdError(value.length > 0 && !isStrongPassword(value));
            setConfirmPswdError(value !== formValues.confirmNewPswd && formValues.confirmNewPswd !== "");
        }

        if (field === "confirmNewPswd") {
            value = value;
            setConfirmPswdError(value !== formValues.newPswd);
        }

        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddAllParks = () => {
        const remainingParks = availableParks.filter(
            (park) => !userParks.some((userPark) => userPark.id === park.id)
        );
        setUserParks((prev) => [...prev, ...remainingParks]);
    };

    const handleDeletePark = (parkName) => {
        // Supprimer le parc de userParks en fonction du nom (ou de l'id)
        setUserParks((prev) => prev.filter((park) => park.name !== parkName));
    };

    const handleReturnClick = () => {
        setUserFormInfosAdm({
            selectedUser: null,
            formType: "Tab",
        });
        console.log("Return Tab");
    };

    const handleCreateNewUser = () => {
        let finalParkIds = [];

        if (currentRole === "AdminAT" && (formValues.role === "AdminAT" || formValues.role === "AdminEnterprise")) {
            finalParkIds = availableParks.map((park) => park.id);
        } else if (formValues.role === "AdminPark") {
            finalParkIds = userParks.map((park) => park.id);
        }

        postAPI({
            requestType: APIRequestType.CREATE_NEW_USER,
            email: formValues.email,
            phone: `0033${formValues.phone.replace(/^0/, "")}`, // Ajoute "0033" et enlève le 0 initial
            name: formValues.name,
            lastName: formValues.lastName,
            pswd: formValues.newPswd,
            role: currentRole === "AdminAT" ? formValues.role : "AdminPark",
            status: "ACTIVE",
            parkId: JSON.stringify(finalParkIds),
        });
    };

    return (
        <MDBox mt={5} mb={1}>
            <Grid container spacing={1}>
                <Grid item xs={24} md={12} xl={6} sx={{ display: "flex" }}>
                    <MDBox display="flex" flexDirection="column" width="100%" px={2}>
                        {currentRole === "AdminAT" && (
                            <MDBox mb={2} width="55%">
                                <FormControl fullWidth>
                                    <Select
                                        value={formValues.role || ""}
                                        onChange={handleChange("role")}
                                        displayEmpty
                                        error={error}
                                        IconComponent={KeyboardArrowDownIcon}
                                        sx={{
                                            borderRadius: "5px",
                                            minHeight: "45px",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return (
                                                    <span style={{ color: "#9e9e9e" }}>
                                                        Select a Role...
                                                    </span>
                                                );
                                            }
                                            return (
                                                <span style={{ color: "#344767" }}>
                                                    {selected}
                                                </span>
                                            );
                                        }}
                                    >
                                        {/* Placeholder */}
                                        <MenuItem value="" disabled>
                                            <span style={{ color: "#9e9e9e" }}>
                                                Select a Role...
                                            </span>
                                        </MenuItem>

                                        {/* Liste des rôles */}
                                        {["AdminAT", "AdminEnterprise", "AdminPark"].map((role) => (
                                            <MenuItem key={role} value={role}>
                                                <span style={{ color: "dark" }}>{role}</span>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </MDBox>
                        )}
                        <MDBox display="flex" width="96%" mb={2} justifyContent="space-between">
                            <MDBox width="48%">
                                <MDInput
                                    type="text"
                                    label="Last Name"
                                    fullWidth
                                    value={formValues.lastName}
                                    inputProps={{ color: "dark" }}
                                    onChange={handleChange("lastName")}
                                    error={error}
                                    InputLabelProps={{ shrink: true }} // Force le label à rester au-dessus même si l'input est vide
                                    sx={{ borderRadius: "5px" }}
                                />
                            </MDBox>
                            <MDBox width="48%">
                                <MDInput
                                    type="text"
                                    label="First Name"
                                    fullWidth
                                    value={formValues.name}
                                    inputProps={{ color: "dark" }}
                                    onChange={handleChange("name")}
                                    error={error}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ borderRadius: "5px" }}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox mb={2} width="40%">
                            <MDInput
                                type="tel"
                                label="Phone"
                                fullWidth
                                value={formValues.phone}
                                onChange={handleChange("phone")}
                                error={phoneError}
                                helperText={phoneError ? "Invalid Format." : ""}
                                inputProps={{ color: "dark", maxLength: 9 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: "1rem" }}>
                                                +33
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ borderRadius: "5px" }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="70%">
                            <MDInput
                                type="email"
                                label="Email"
                                fullWidth
                                value={formValues.email}
                                onChange={handleChange("email")}
                                error={emailError}
                                inputProps={{ color: "dark" }}
                                InputLabelProps={{ shrink: true }}
                                helperText={emailError ? "Invalid Format." : ""}
                                sx={{
                                    borderRadius: "5px",
                                }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="55%">
                            <MDInput
                                type="password"
                                label="New Password"
                                fullWidth
                                value={formValues.newPswd}
                                onChange={handleChange("newPswd")}
                                error={newPswdError}
                                helperText={newPswdError ? "8 characters, number, upper case, lower case." : ""}
                                inputProps={{ color: "dark" }}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    borderRadius: "5px",
                                }}
                            />
                        </MDBox>
                        <MDBox mb={2} width="55%">
                            <MDInput
                                type="password"
                                label="Confirm New Password"
                                fullWidth
                                value={formValues.confirmNewPswd}
                                onChange={handleChange("confirmNewPswd")}
                                error={confirmPswdError}
                                helperText={confirmPswdError ? "Password not similar." : ""}
                                inputProps={{ color: "dark" }}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    borderRadius: "5px",
                                }}
                            />
                        </MDBox>
                    </MDBox>
                </Grid>
                <Grid item xs={24} md={12} xl={5.8}>
                    {formValues.role === "AdminPark" && (
                        <MDBox display="flex" alignItems="center" gap={3}>
                            <MDBox width="55%">
                                <FormControl fullWidth>
                                    <Select
                                        value={selectedPark}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            setSelectedPark("");
                                            const selectedParkObj = availableParks.find(park => park.name === selected);
                                            if (selectedParkObj && !userParks.some(park => park.id === selectedParkObj.id)) {
                                                setUserParks(prev => [...prev, selectedParkObj]);
                                            }
                                        }}
                                        displayEmpty
                                        error={error}
                                        IconComponent={KeyboardArrowDownIcon}
                                        sx={{
                                            borderRadius: "5px",
                                            minHeight: "45px",
                                        }}
                                        renderValue={(selected) => {
                                            if (!selected) {
                                                return <span style={{ color: "#9e9e9e" }}>Select a park to add...</span>;
                                            }
                                            return <span style={{ color: "#344767" }}>{selected}</span>;
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            <span style={{ color: "#9e9e9e" }}>Select a park to add...</span>
                                        </MenuItem>
                                        {availableParks.map((park) => (
                                            <MenuItem key={park.id} value={park.name}>
                                                <span style={{ color: "dark" }}>{park.name}</span>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </MDBox>
                            <MDButton
                                sx={{ height: "45px", '&:hover': { backgroundColor: "#f0f2f5" } }}
                                onClick={handleAddAllParks}
                            >
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDTypography variant="h7" color="secondary" fontWeight="medium">
                                        ADD ALL PARKS ACCESS
                                    </MDTypography>
                                    <Icon color="secondary" fontSize="large">add</Icon>
                                </MDBox>
                            </MDButton>
                        </MDBox>
                    )}

                    {/* Tableau affichant les parcs de l'utilisateur */}
                    <MDBox pt={formValues.role === "AdminPark" ? 6 : 3} pb={2.5}>
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Card>
                                    <MDBox
                                        mx={2}
                                        mt={-3}
                                        py={1.5}
                                        px={2}
                                        variant="gradient"
                                        bgColor="info"
                                        borderRadius="lg"
                                        coloredShadow="info"
                                    >
                                        <MDTypography variant="h6" color="white">
                                            Parks Access
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox pt={3}>
                                        <DataTable
                                            table={{
                                                columns: [
                                                    { Header: "", accessor: "name", align: "left" },
                                                    { Header: "", accessor: "icon", align: "right" },
                                                ],
                                                rows: formValues.role === "AdminPark"
                                                    ? (userParks.length === 0
                                                        ? [{
                                                            nameValue: "...",
                                                            name: (
                                                                <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
                                                                    ...
                                                                </MDTypography>
                                                            ),
                                                        }]
                                                        : userParks.map((park) => ({
                                                            nameValue: park.name,
                                                            name: (
                                                                <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
                                                                    {park.name}
                                                                </MDTypography>
                                                            ),
                                                            icon: (
                                                                <MDBox>
                                                                    <Icon
                                                                        fontSize="small"
                                                                        sx={{ mt: -0.25, '&:hover': { color: "#b7e4fd" } }}
                                                                        onClick={() => handleDeletePark(park.name)}
                                                                    >
                                                                        delete
                                                                    </Icon>
                                                                </MDBox>
                                                            ),
                                                        }))
                                                    )
                                                    : ["AdminAT", "AdminEnterprise", "AdminPark"].includes(formValues.role)
                                                        ? [{
                                                            nameValue: "ALL PARK ACCESS",
                                                            name: (
                                                                <MDTypography component="span" variant="caption" color="dark" fontWeight="medium">
                                                                    ALL PARKS ACCESS
                                                                </MDTypography>
                                                            ),
                                                            icon: null,
                                                        }]
                                                        : [{
                                                            nameValue: "...",
                                                            name: (
                                                                <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
                                                                    ...
                                                                </MDTypography>
                                                            ),
                                                        }],
                                            }}
                                            rowsNumberPerPage={5}
                                            isSorted={false}
                                            entriesPerPage={false}
                                            showTotalEntries={false}
                                            noEndBorder
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Grid>

            </Grid>
            <Grid item xs={24} md={12} xl={6} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <MDBox display="flex" flexDirection="row" gap={3} justifyContent="center" alignItems="center">
                    <MDBox mt={1} mb={1} flexGrow={1}>
                        <MDButton
                            type="submit"
                            variant="gradient"
                            color="info"
                            onClick={handleCreateNewUser}
                            fullWidth
                            disabled={!isFormValid()} // <--- Désactive le bouton si invalide
                            sx={{ width: '150px' }}
                        >
                            CREATE
                        </MDButton>
                    </MDBox>
                    <MDBox mt={1} mb={1} flexGrow={1}>
                        <MDButton
                            type="submit"
                            variant="outlined"
                            color="secondary"
                            onClick={handleReturnClick}
                            fullWidth
                            sx={{ width: '150px' }}
                        >
                            CANCEL
                        </MDButton>
                    </MDBox>
                </MDBox>
            </Grid>
        </MDBox>
    );
}

export default Create;